import React, { Suspense, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { MasterDataProvider } from "../contexts/MasterDataProvider";
import { CustomerPlatformStatus, PlatformStatusHistory } from "../types/customer/CustomerPlatformStatus";
import { Customer, PlatformDetail } from "../types/customer";
import customerService from "../services/customer.service";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import useBranding from "../hooks/useBranding";
import { AUTH_BASE_URL, AUTH_CLIENT_ID } from "../constants";
import { appUrl } from "..";
import useLocale from "../hooks/useLocale";
import LoadingOverlay from "./LoadingOverlay";
import { LocalStorageKeys } from "../enums/localStorageKeys";
import {SessionStorageKeys} from "../enums/sessionStorageKeys"
import SettingService from "../services/setting.service";
import jwt_decode from "jwt-decode";
import { TokenPayload } from "../types/context/TokenPayload";
import FloatOverlay from "./layout/float/FloatOverlay";

const ProtectedRoutes: React.FC = () => {
  const location = useLocation();
  const localeCtx = useLocale();
  const auth = useAuth();
  const navigate = useNavigate();
  const brandingCtx = useBranding();
  const [msTeamsPlatformStatus, setMsTeamsPlatformStatus] = useState<CustomerPlatformStatus>();
  const [customerDetails, setCustomerDetails] = useState<Customer>();
  const [platformStatusLoading, setPlatformStatusLoading] = useState<boolean>(true);
  const [customerLoading, setCustomerLoading] = useState<boolean>(true);
  const [showAlert, setShowAlert] = useState(false);
  
  useEffect(() => {
    getMsTeamsPlatformDetails();
    getCustomerDetails();
    getInstanceConfigs();
  }, [auth?.tokenPayload?.CustomerId])

  useEffect(() => {
    if (!msTeamsPlatformStatus || (msTeamsPlatformStatus.platformName === "MsTeams" &&
      !msTeamsPlatformStatus.platformStatusHistory.find((platformHistory: PlatformStatusHistory) => platformHistory.statusName === "PlatformActive"))) {
      getMsTeamsPlatformDetails();
    }
  }, [navigate])

  const getInstanceConfigs = async() => {
    try{
      const data = await SettingService.fetchInstanceConfigs();
      window.sessionStorage.setItem("InstanceConfigs",JSON.stringify(data));
    }catch(err){
      console.log("error",err);
    }
  }

  const getMsTeamsPlatformDetails = async () => {
    try {
      setPlatformStatusLoading(true);
      const msTeamsStatus = await customerService.getCustomerPlatformStatus(auth?.tokenPayload?.CustomerId ?? 0, "MsTeams");
      setMsTeamsPlatformStatus(msTeamsStatus);
      setShowAlert(msTeamsStatus?.platformIsSuspended);
    } catch (error) {
      console.log(error);
    }
    finally {
      setPlatformStatusLoading(false);
    }
  }

  const getCustomerDetails = async () => {
    try {
      setCustomerLoading(true);
      const customerDetails = await customerService.getCustomerDetailsById(auth?.tokenPayload?.CustomerId ?? 0);
      setCustomerDetails(customerDetails)
    } catch (error) {
      console.log(error);
    }
    finally{
      setCustomerLoading(false);
    }
  }

  const logoutHandler = async () => {
    let authAppBaseUrl = AUTH_BASE_URL;
    if (brandingCtx?.branding?.authAppConfiguration.authClientAppUrl) {
      authAppBaseUrl =
        brandingCtx.branding.authAppConfiguration.authClientAppUrl;
    }
    let authAppClientId = AUTH_CLIENT_ID;
    if (brandingCtx?.branding?.authAppConfiguration.authAppClientId) {
      authAppClientId =
        brandingCtx.branding.authAppConfiguration.authAppClientId;
    }

    try {
      const voiceAIToken = localStorage.getItem("VoiceAIToken");
      let isDebuggingToken = false;
      if (voiceAIToken) {
        try {
          const claims = jwt_decode<TokenPayload>(voiceAIToken);
          isDebuggingToken = !!claims?.IsDebuggingToken;
        } catch { }
      }

      window.localStorage.removeItem(
        LocalStorageKeys.VoiceAIInsightsFilter
      );
      window.sessionStorage.removeItem(SessionStorageKeys.insightId);
      sessionStorage.removeItem("VoiceAiCallFilter");
      sessionStorage.removeItem("ShareToken");
      localStorage.removeItem("VoiceAIToken");

      Object.keys(sessionStorage)
      .filter((key) => key.startsWith("chatHistory_"))
      .forEach((key) => sessionStorage.removeItem(key));

      if (isDebuggingToken) {
        window.location.replace(`${appUrl}/login`);
      } else {
        window.location.replace(
          `${authAppBaseUrl}/logout?clientId=${authAppClientId}&redirectUri=${appUrl}/login&language=${localeCtx?.selectedLocale?.current.locale.code}`
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const grantPermissionHandler=()=>{
    window.open("/customer-platform",'_blank','noopener noreferrer')
    }

  return auth?.accessToken ? (
    <>
    <div className="float-left w-100 p-t-55 h-100">
        <div className="mobile-contain h-100 pt-4 p-l-15 p-r-15 ">
          <div className="container-fluid h-100 pb-0">
          {showAlert && ( 
            <div className="alert alert-danger suspension d-flex align-items-center justify-content-between pl-3 pr-2 py-2 mb-3" style={{ zIndex: "100" }}>
              <span>
                Recording has been suspended. Recording will be resumed when your Microsoft administrator has updated payment details.
              </span>
            </div>
          )} 
          <div className="row h-100">
            <div className="col-md-12 h-100">
              <MasterDataProvider>
                <FloatOverlay/>
                <Outlet />
              </MasterDataProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
    {platformStatusLoading || customerLoading ? 
      <LoadingOverlay
        customStyle={{ position: "fixed", marginTop: "55px" }}
        themeColor={"light"}
        size={"medium"}
        loadingText={""}
      />
      :
      location.pathname!=="/customer-platform" && location.pathname!=="/customer-platform/admin-action" && customerDetails && msTeamsPlatformStatus && customerDetails.platformDetails.length===1 &&
      customerDetails.platformDetails.find((platform:PlatformDetail)=>platform.platform==="MsTeams") &&
      msTeamsPlatformStatus.platformName==="MsTeams" &&
      !msTeamsPlatformStatus.platformStatusHistory.find((platformHistory:PlatformStatusHistory)=> platformHistory.statusName==="PlatformActive") &&
        <Dialog title="Authorize Synchronization" className="dialogAuth">
          <div className="row">
            <div className="col">
              <img src="https://tollringtest.blob.core.windows.net/voiceailogo/A365_RX_Logo.png" alt="A365 Logo" className="logo" />
            </div>
          </div>
          <hr />
          <div className="row" style={{ maxWidth: "800px" }}>
            <div className="col">
              <p>To activate recording, we need your Microsoft Administrator to authorize synchronization with Microsoft 365.</p> <p>Contact your Microsoft Admin or Grant Permission if you have Microsoft Admin permissions</p>
              <button onClick={grantPermissionHandler} disabled={!customerDetails.isActive || msTeamsPlatformStatus.platformIsSuspended} className='btn btn-sm bg-primary text-white mb-3'>Grant Permission now!</button>
            </div>
          </div>
          <DialogActionsBar>
            <Button onClick={logoutHandler}>Log Out!</Button>
          </DialogActionsBar>
        </Dialog>
    }
    </>
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  )
};

export default ProtectedRoutes;
